import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FormInputsConfig, TextInput } from './SignupFormInputs';
import { SignupReason } from './SignupCardV2';
import { Cloud, Edition, FeatureFlag, isCortexCodeFlow } from '@signup/shared';
import { isFeatureEnabled } from '../../../../utils/configurations';
import { useSignupFormData } from './SignupFormDataContext';
import { useSignupFormController } from './SignupFormControllerContext';
import { useSortedRegionSelectList } from './regionGroupingConfigs';
import { useIsPhoneNumberRequired } from '../../utils/SignupCountryUtils';
import { parseQueryParams } from '../../../../utils/ParseQueryParams';
import { useSignupFlowType } from '../../../../utils/useSignupFlowType';

export function useStepOneInputConfigs(): FormInputsConfig {
  const { formatMessage } = useIntl();
  const isPhoneNumberRequired = useIsPhoneNumberRequired();

  const inputConfigs: FormInputsConfig = useMemo(
    () => [
      [
        {
          title: formatMessage({ id: 'First name' }),
          fieldName: 'firstName',
          type: 'text',
        },
        {
          title: formatMessage({ id: 'Last name' }),
          fieldName: 'lastName',
          type: 'text',
        },
      ],
      [
        {
          title: formatMessage({ id: 'Work email' }),
          fieldName: 'email',
          type: 'text',
        },
      ],
      ...(isPhoneNumberRequired
        ? [
            [
              {
                title: formatMessage({ id: 'Phone number' }),
                fieldName: 'phoneNumber',
                type: 'text',
              } as TextInput,
            ],
          ]
        : []),
      [
        {
          title: formatMessage({ id: 'Why are you signing up?' }),
          fieldName: 'signupReason',
          type: 'selector',
          options: [
            {
              value: SignupReason.CompanyConsidering,
              label: formatMessage({ id: 'Company is considering Snowflake' }),
            },
            {
              value: SignupReason.JobTraining,
              label: formatMessage({ id: 'Training or certification for my job' }),
            },
            {
              value: SignupReason.PersonalTraining,
              label: formatMessage({ id: 'Personal learning and development' }),
            },
            {
              value: SignupReason.Student,
              label: formatMessage({ id: 'I’m a student exploring Snowflake' }),
            },
            {
              value: SignupReason.Other,
              label: formatMessage({ id: 'Other' }),
            },
          ],
        },
      ],
    ],
    [formatMessage, isPhoneNumberRequired],
  );

  return inputConfigs;
}

const MODIFIED_FLOW_STATIC_FIELDS: FormInputsConfig = [
  [
    {
      title: 'First name',
      fieldName: 'firstName',
      type: 'text',
    },
    {
      title: 'Last name',
      fieldName: 'lastName',
      type: 'text',
    },
  ],
  [
    {
      title: 'Work email',
      fieldName: 'email',
      type: 'text',
    },
  ],
  [
    {
      title: 'Company name',
      fieldName: 'companyName',
      type: 'text',
    },
  ],
  [
    {
      title: 'Job title',
      fieldName: 'jobTitle',
      type: 'text',
    },
  ],
];

export function useStepOneModifiedInputConfigs(): FormInputsConfig {
  const signupFlowType = useSignupFlowType();

  return useMemo(
    () =>
      MODIFIED_FLOW_STATIC_FIELDS.map(row =>
        row.map(field =>
          field.fieldName === 'email' && isCortexCodeFlow(signupFlowType)
            ? { ...field, title: 'Email' }
            : field,
        ),
      ),
    [signupFlowType],
  );
}

const WAITLIST_STATIC_FIELDS: FormInputsConfig = [
  [
    {
      title: 'First name',
      fieldName: 'firstName',
      type: 'text',
    },
    {
      title: 'Last name',
      fieldName: 'lastName',
      type: 'text',
    },
  ],
  [
    {
      title: 'Work email',
      fieldName: 'email',
      type: 'text',
    },
  ],
];

export function useWaitlistInputConfigs(): FormInputsConfig {
  return WAITLIST_STATIC_FIELDS;
}

export function useStudentInputConfigs(): FormInputsConfig {
  const { formatMessage } = useIntl();

  return useMemo(
    () => [
      [
        {
          title: formatMessage({ id: 'School name' }),
          fieldName: 'schoolName',
          type: 'text',
        },
      ],
    ],
    [formatMessage],
  );
}

export function useNonStudentInputConfigs(): FormInputsConfig {
  const { formatMessage } = useIntl();

  return useMemo(
    () => [
      [
        {
          title: formatMessage({ id: 'Company name' }),
          fieldName: 'companyName',
          type: 'text',
        },
      ],
      [
        {
          title: formatMessage({ id: 'Job title' }),
          fieldName: 'jobTitle',
          type: 'text',
        },
      ],
    ],
    [formatMessage],
  );
}

export function useEditionAndCloudInputConfigs(): FormInputsConfig {
  const { formatMessage } = useIntl();
  const { cloud } = useSignupFormData();
  const { regionSelectList, showVisibleRegionsFilter, signupCardProps } = useSignupFormController();
  const groupedRegionSelectList = useSortedRegionSelectList();
  const signupFlowType = useSignupFlowType();

  const { lockedCloud, lockedRegion } = useMemo(() => {
    const [signupParams, _] = parseQueryParams(window.location.search);
    const { cloud: lockedCloud, region: lockedRegion } = signupParams;
    return { lockedCloud, lockedRegion };
  }, []);

  const logos: { [cloud in Cloud]: { src: string; title: string } } = useMemo(() => {
    return {
      [Cloud.AWS]: {
        src: '/static/images/cloud_provider_choices/aws-logo.svg',
        title: formatMessage({ id: 'Amazon Web Services' }),
      },
      [Cloud.GCP]: {
        src: '/static/images/cloud_provider_choices/gcp-logo.png',
        title: formatMessage({ id: 'Google Cloud Platform' }),
      },
      [Cloud.AZURE]: {
        src: '/static/images/cloud_provider_choices/microsoft-logo.svg',
        title: formatMessage({ id: 'Microsoft Azure' }),
      },
    };
  }, [formatMessage]);

  const cloudConfigs: FormInputsConfig = useMemo(() => {
    // For Cortex Code flows, only show AWS since all supported regions are AWS-only
    const availableCloudOptions = isCortexCodeFlow(signupFlowType)
      ? [
          {
            src: logos[Cloud.AWS].src,
            title: logos[Cloud.AWS].title,
            value: Cloud.AWS,
          },
        ]
      : [
          {
            src: logos[Cloud.AZURE].src,
            title: logos[Cloud.AZURE].title,
            value: Cloud.AZURE,
          },
          {
            src: logos[Cloud.AWS].src,
            title: logos[Cloud.AWS].title,
            value: Cloud.AWS,
          },
          {
            src: logos[Cloud.GCP].src,
            title: logos[Cloud.GCP].title,
            value: Cloud.GCP,
          },
        ];

    return [
      [
        {
          title: formatMessage({ id: 'Choose your Snowflake edition' }),
          rightHandSideFloater: {
            text: formatMessage({ id: 'Learn more' }),
            link: 'https://www.snowflake.com/en/pricing-options/#pricingEditions',
          },
          fieldName: 'edition',
          type: 'selector',
          options: [
            {
              value: Edition.STANDARD,
              label: formatMessage({ id: 'Standard' }),
              sublabel: formatMessage({
                id: 'A strong balance between features, level of support, and cost',
              }),
            },
            {
              value: Edition.ENTERPRISE,
              label: formatMessage({ id: 'Enterprise (Most popular)' }),
              sublabel: formatMessage({
                id: 'Standard plus 90-day time travel, multi-cluster warehouses, and materialized views',
              }),
            },
            {
              value: Edition.BUSINESS_CRITICAL,
              label: formatMessage({ id: 'Business Critical' }),
              sublabel: formatMessage({
                id: 'Enterprise plus enhanced security, data protection, and database fallover/fallback',
              }),
            },
          ],
        },
      ],
      [
        {
          title: lockedCloud
            ? formatMessage({ id: 'Cloud provider' })
            : formatMessage({ id: 'Choose your cloud provider' }),
          subtitle: formatMessage({
            id: 'Snowflake runs on top of the cloud platform you are already working with.',
          }),
          fieldName: 'cloud',
          type: 'cloud',
          options: lockedCloud
            ? [
                {
                  src: logos[lockedCloud].src,
                  title: logos[lockedCloud].title,
                  value: lockedCloud,
                },
              ]
            : availableCloudOptions,
        },
      ],
    ];
  }, [formatMessage, lockedCloud, logos, signupFlowType]);

  return useMemo(() => {
    const configs = [...cloudConfigs];
    const isCortexCodeV2 = isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE_2026);

    if (cloud) {
      // Add region filter toggle if listing parameter exists and filter should be shown
      if (signupCardProps.signupParams.listing && showVisibleRegionsFilter) {
        configs.push([
          {
            title: '',
            fieldName: 'filterVisibleRegions',
            type: 'regionFilterToggle',
          },
        ]);
      }

      configs.push([
        {
          title: formatMessage({ id: 'Region' }),
          subtitle: isCortexCodeFlow(signupFlowType)
            ? isCortexCodeV2
              ? formatMessage({
                  id: 'Note: Snowflake CoCo is currently available in select regions. Please choose from the available options below.',
                })
              : formatMessage({
                  id: 'Note: Cortex Code CLI is currently available in select regions. Please choose from the available options below.',
                })
            : undefined,
          fieldName: 'region',
          type: 'selector',
          options: lockedRegion
            ? regionSelectList[cloud].filter(regionOption => regionOption.value === lockedRegion)
            : groupedRegionSelectList || regionSelectList[cloud],
        },
      ]);
    }

    return configs;
  }, [
    cloudConfigs,
    cloud,
    signupCardProps.signupParams.listing,
    showVisibleRegionsFilter,
    formatMessage,
    lockedRegion,
    regionSelectList,
    groupedRegionSelectList,
    signupFlowType,
  ]);
}
